import dayjs from 'dayjs';

import {
  type MarketingRouterTreatmentType,
  getMarketingRouterTreatmentConfig,
  marketingRouterTreatmentTypes,
} from '@luxuryescapes/contract-svc-promo/marketing-router/admin';

import type { MarketingRouterRule } from '~/types/marketingRouterRule';

import { toMatchValues } from './marketingRouterMatch';

/**
 * Search and filter state for the rules list.
 *
 * Filtering runs over the brand's rule list already held by the page: the
 * internal endpoint only knows brand, region and active/expired, and has no
 * name search, so nothing here talks to the server. Every function is pure
 * so the behaviour is unit-tested without rendering.
 *
 * Dates are Sydney calendar days as `YYYY-MM-DD` strings — the shape the
 * rule records already carry — so comparisons are plain string comparisons
 * and match the runtime's inclusive-both-ends window.
 */

export type MarketingRouterRuleStatusFilter = '' | 'scheduled' | 'running' | 'ended';

export type MarketingRouterRuleFilters = {
  /** Case-insensitive substring of the rule name or id. */
  search: string;
  status: MarketingRouterRuleStatusFilter;
  /** Keep rules whose date window contains this day. */
  activeOn: string;
  /** '' for any, ANYWHERE_REGION_FILTER for rules with no regions, else a region code. */
  region: string;
  /** Case-insensitive substring over every configured match value. */
  match: string;
  treatment: '' | MarketingRouterTreatmentType;
};

export const ANYWHERE_REGION_FILTER = 'anywhere';

export const EMPTY_MARKETING_ROUTER_RULE_FILTERS: MarketingRouterRuleFilters = {
  search: '',
  status: '',
  activeOn: '',
  region: '',
  match: '',
  treatment: '',
};

export type MarketingRouterRuleStatusLabel = 'Scheduled' | 'Running' | 'Ended';

/**
 * Status colours, shared by the rules table chips and the timeline bars so the
 * two views can never disagree. `chipColor` is an MUI Chip colour name; `palette`
 * is the theme palette path for a solid fill (e.g. a timeline bar).
 */
export const MARKETING_ROUTER_STATUS_STYLE: Record<
  MarketingRouterRuleStatusLabel,
  { chipColor: 'default' | 'success' | 'info'; palette: string }
> = {
  Scheduled: { chipColor: 'default', palette: 'grey.500' },
  Running: { chipColor: 'success', palette: 'success.main' },
  Ended: { chipColor: 'info', palette: 'info.main' },
};

export const MARKETING_ROUTER_RULE_STATUS_OPTIONS: Array<{
  value: Exclude<MarketingRouterRuleStatusFilter, ''>;
  label: MarketingRouterRuleStatusLabel;
}> = [
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'running', label: 'Running' },
  { value: 'ended', label: 'Ended' },
];

type FilterableRule = Pick<MarketingRouterRule, 'id' | 'name' | 'start_date' | 'end_date' | 'match'> & {
  variants: Array<{ treatments?: Array<{ type: string }> }>;
};

/** Where a rule sits relative to `today`, both dates inclusive. */
export function getMarketingRouterRuleStatus(
  rule: Pick<FilterableRule, 'start_date' | 'end_date'>,
  today: string,
): MarketingRouterRuleStatusLabel {
  if (today < rule.start_date) {
    return 'Scheduled';
  }
  if (today > rule.end_date) {
    return 'Ended';
  }
  return 'Running';
}

function isStatusFilter(value: string): value is MarketingRouterRuleStatusFilter {
  return value === '' || MARKETING_ROUTER_RULE_STATUS_OPTIONS.some((option) => option.value === value);
}

function isTreatmentFilter(value: string): value is MarketingRouterRuleFilters['treatment'] {
  return value === '' || marketingRouterTreatmentTypes.some((type) => type === value);
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

export function parseMarketingRouterRuleFilters(params: URLSearchParams): MarketingRouterRuleFilters {
  const status = params.get('status') ?? '';
  const treatment = params.get('treatment') ?? '';
  const activeOn = params.get('activeOn') ?? '';

  return {
    search: params.get('search') ?? '',
    status: isStatusFilter(status) ? status : '',
    activeOn: ISO_DAY.test(activeOn) ? activeOn : '',
    region: params.get('region') ?? '',
    match: params.get('match') ?? '',
    treatment: isTreatmentFilter(treatment) ? treatment : '',
  };
}

/** Only the applied filters, so a clean list has a clean URL. */
export function toMarketingRouterRuleFilterParams(filters: MarketingRouterRuleFilters): URLSearchParams {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value) {
      params.set(key, value);
    }
  }
  return params;
}

export function hasActiveMarketingRouterRuleFilters(filters: MarketingRouterRuleFilters): boolean {
  return Object.values(filters).some((value) => value !== '');
}

function includesIgnoringCase(haystack: string, needle: string): boolean {
  return haystack.toLowerCase().includes(needle.toLowerCase());
}

const MATCH_TEXT_FIELDS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'url'] as const;

function getMatchTextValues(match: FilterableRule['match']): Array<string> {
  return MATCH_TEXT_FIELDS.flatMap((field) => toMatchValues(match[field]));
}

function matchesRegion(rule: FilterableRule, region: string): boolean {
  const regions = toMatchValues(rule.match.regions);
  if (region === ANYWHERE_REGION_FILTER) {
    return regions.length === 0;
  }
  // A rule with no regions serves every region, so it is kept for any code.
  return regions.length === 0 || regions.includes(region);
}

export function filterMarketingRouterRules<T extends FilterableRule>(
  rules: Array<T>,
  filters: MarketingRouterRuleFilters,
  today: string,
): Array<T> {
  const search = filters.search.trim();
  const match = filters.match.trim();

  return rules.filter((rule) => {
    if (search && !includesIgnoringCase(rule.name, search) && !includesIgnoringCase(rule.id, search)) {
      return false;
    }
    if (filters.status && getMarketingRouterRuleStatus(rule, today).toLowerCase() !== filters.status) {
      return false;
    }
    if (filters.activeOn && !(rule.start_date <= filters.activeOn && filters.activeOn <= rule.end_date)) {
      return false;
    }
    if (filters.region && !matchesRegion(rule, filters.region)) {
      return false;
    }
    if (match && !getMatchTextValues(rule.match).some((value) => includesIgnoringCase(value, match))) {
      return false;
    }
    if (
      filters.treatment &&
      !rule.variants.some((variant) =>
        (variant.treatments ?? []).some((treatment) => treatment.type === filters.treatment),
      )
    ) {
      return false;
    }
    return true;
  });
}

export type MarketingRouterRuleFilterChip = {
  key: keyof MarketingRouterRuleFilters;
  label: string;
};

/** One chip per applied filter, worded as "Name: value". */
export function getMarketingRouterRuleFilterChips(
  filters: MarketingRouterRuleFilters,
): Array<MarketingRouterRuleFilterChip> {
  const chips: Array<MarketingRouterRuleFilterChip> = [];

  if (filters.search.trim()) {
    chips.push({ key: 'search', label: `Search: ${filters.search.trim()}` });
  }
  if (filters.status) {
    const option = MARKETING_ROUTER_RULE_STATUS_OPTIONS.find((candidate) => candidate.value === filters.status);
    chips.push({ key: 'status', label: `Status: ${option?.label ?? filters.status}` });
  }
  if (filters.activeOn) {
    chips.push({ key: 'activeOn', label: `Active on: ${dayjs(filters.activeOn).format('DD MMM YYYY')}` });
  }
  if (filters.region) {
    chips.push({
      key: 'region',
      label: `Region: ${filters.region === ANYWHERE_REGION_FILTER ? 'Anywhere' : filters.region}`,
    });
  }
  if (filters.match.trim()) {
    chips.push({ key: 'match', label: `Match: ${filters.match.trim()}` });
  }
  if (filters.treatment) {
    chips.push({
      key: 'treatment',
      label: `Treatment: ${getMarketingRouterTreatmentConfig(filters.treatment).typeName}`,
    });
  }

  return chips;
}
